import React, { useEffect, useState, useCallback, useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'

// MUI Imports
import {
  Drawer,
  Button,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  Paper,
  Tooltip
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import ClearAllIcon from '@mui/icons-material/ClearAll'

// Component Imports
import CampaignInformation from '../molecules/CampaignInformation'

// AI Template Components
import BusinessCompanyInfoSection from '../molecules/BusinessCompanyInfoSection'
import EmailPurposeTargetingSection from '../molecules/EmailPurposeTargetingSection'

import { useSDSsearchParams } from '../../../../../../src/store/searchDataStore'
import { useGlobalStoreActions, useGlobalStoreData } from '../../../../../../src/store/globalStore'

import { createCampaign } from '../../../services/B2BServices'
import { useToast } from '../../../../../campaigns/hooks/useToast'
import { useCountries } from '../../../../../campaigns/hooks/useCountries'

import { countryEN } from '../../../../../assets/data/country-en'
import { countryTR } from '../../../../../assets/data/country-tr'
import { TRADE_TYPES, LANGUAGES } from '../../../utils/campaignConstants'
import { getUserDetailsAndProcess } from '../../../../../services/DataServiceMethods'

const CreateCampaignDrawer = (props) => {
  const { open, setOpen, b2bAccountId, fetchCampaigns, userGSD } = props

  const { t } = useTranslation()
  const { showError } = useToast()
  const { countries } = useCountries()

  const currentUser = useGlobalStoreData.user()
  const currentLang = useGlobalStoreData.language()

  const searchParams = useSDSsearchParams()

  const [loading, setLoading] = useState(false)
  const [autofillLoading, setAutofillLoading] = useState(false)

  const { setUserData } = useGlobalStoreActions()

  const defaultValues = useMemo(() => ({
    name: '',
    importerCountryCode: (searchParams?.countryToExport && countries?.length)
      ? {
          label: countries.find((item) => item.countryCode === searchParams.countryToExport)?.label,
          countryCode: searchParams.countryToExport
        }
      : null,
    currentCountryCode: (searchParams?.yourCountry && countries?.length)
      ? {
          label: countries.find((item) => item.countryCode === searchParams.yourCountry)?.label,
          countryCode: searchParams.yourCountry
        }
      : null,
    hsCodes: searchParams?.hsCode ? searchParams.hsCode.split(',').map((item) => ({ label: item, value: item })) : [],
    productName: searchParams?.productDesc ? [{ value: searchParams.productDesc }] : [],

    sender_company: {
      name: '',
      industry: '',
      country: '',
      contact_person: '',
      contact_title: '',
      email: currentUser?.emailSettings?.emailUserName || '',
      key_products: [],
      website: '',
      phone: '',
      production_capacity: '',
      facilities: [],
      certifications: [],
      market_experience: [],
      company_values: []
    },
    email_purpose: {
      target_market: '',
      trade_type: TRADE_TYPES.EXPORT,
      product_category: '',
      language: LANGUAGES.ENGLISH,
      specific_products: [],
      call_to_action: ''
    },
    attachments: {
      pdf_files: [],
      catalog_description: '',
      sample_availability: false
    }
  }), [searchParams, countries, currentUser])

  const {
    control,
    reset,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues,
    mode: 'onChange'
  })

  const watchedFields = watch([
    'name',
    'hsCodes',
    'importerCountryCode',
    'productName',
    'currentCountryCode',
    'sender_company.name',
    'sender_company.industry',
    'sender_company.country',
    'sender_company.contact_person',
    'sender_company.contact_title',
    'sender_company.email',
    'sender_company.key_products',
    'email_purpose.target_market',
    'email_purpose.trade_type',
    'email_purpose.product_category',
    'email_purpose.language'
  ])

  const isFormValid = useMemo(() => {
    const [
      name,
      hsCodes,
      importerCountryCode,
      productName,
      currentCountryCode,
      companyName,
      industry,
      country,
      contactPerson,
      contactTitle,
      email,
      keyProducts,
      targetMarket,
      tradeType,
      productCategory,
      language
    ] = watchedFields

    if (!name || !importerCountryCode || !currentCountryCode) {
      return false
    }

    if (!Array.isArray(hsCodes) || hsCodes.length === 0) {
      return false
    }

    if (!Array.isArray(productName) || productName.length === 0) {
      return false
    }

    if (!companyName || !industry || !country || !contactPerson || !contactTitle) {
      return false
    }

    if (!email) {
      return false
    }

    if (!keyProducts || !Array.isArray(keyProducts) || keyProducts.length === 0) {
      return false
    }

    if (!targetMarket || !tradeType || !productCategory || !language) {
      return false
    }

    return true
  }, [watchedFields])

  const handleClose = useCallback(() => {
    reset(defaultValues)
    setOpen(false)
  }, [reset, setOpen, defaultValues])

  const getLocalizedCountryName = useCallback((countryName) => {
    if (!countryName) return ''

    const currentCountries = currentLang === 'en' ? countryEN : countryTR
    const otherCountries = currentLang === 'en' ? countryTR : countryEN

    const foundInCurrent = currentCountries.find(c => c.countryName === countryName)
    if (foundInCurrent) {
      return countryName
    }

    const foundInOther = otherCountries.find(c => c.countryName === countryName)
    if (foundInOther) {
      const translatedCountry = currentCountries.find(c => c.countryCode === foundInOther.countryCode)
      if (translatedCountry) {
        return translatedCountry.countryName
      }
    }

    return countryName
  }, [currentLang])

  const handleAutofill = useCallback(() => {
    if (!currentUser || Object.keys(currentUser).length === 0) {
      showError(t('mailTemplate.campaigns.modal.autofillDataNotAvailable'))
      return
    }

    setAutofillLoading(true)

    try {
      const transformArrayData = (data) => {
        if (!Array.isArray(data)) return []
        return data.map(item => {
          if (typeof item === 'string') {
            return { value: item }
          } else if (typeof item === 'object' && item !== null) {
            return { value: item.value || String(item) }
          }
          return { value: String(item) }
        })
      }

      const currentValues = watch()

      const autofillData = {
        ...currentValues,
        sender_company: {
          ...currentValues.sender_company,
          name: currentUser.senderCompany?.name || '',
          industry: currentUser.senderCompany?.industry || '',
          country: getLocalizedCountryName(currentUser.senderCompany?.country || ''),
          contact_person: currentUser.senderCompany?.contact_person || '',
          contact_title: currentUser.senderCompany?.contact_title || '',
          email: currentUser.emailSettings?.emailUserName || '',
          key_products: transformArrayData(currentUser.senderCompany?.key_products || []),
          website: currentUser.senderCompany?.website || '',
          phone: currentUser.senderCompany?.phone || '',
          production_capacity: currentUser.senderCompany?.production_capacity || '',
          facilities: transformArrayData(currentUser.senderCompany?.facilities || []),
          certifications: transformArrayData(currentUser.senderCompany?.certifications || []),
          market_experience: transformArrayData(currentUser.senderCompany?.market_experience || []),
          company_values: transformArrayData(currentUser.senderCompany?.company_values || [])
        },

        email_purpose: {
          ...currentValues.email_purpose,
          target_market: getLocalizedCountryName(currentUser.emailPurpose?.target_market || ''),
          trade_type: currentUser.emailPurpose?.trade_type || TRADE_TYPES.EXPORT,
          product_category: currentUser.emailPurpose?.product_category || '',
          language: currentUser.emailPurpose?.language || LANGUAGES.ENGLISH,
          specific_products: transformArrayData(currentUser.emailPurpose?.specific_products || []),
          call_to_action: currentUser.emailPurpose?.call_to_action || ''
        },

        attachments: {
          pdf_files: [],
          catalog_description: '',
          sample_availability: false
        }
      }

      reset(autofillData)
    } catch (error) {
      showError(t('mailTemplate.campaigns.modal.autofillDataLoadError'))
    } finally {
      setAutofillLoading(false)
    }
  }, [currentUser, showError, t, watch, reset, getLocalizedCountryName])

  const handleClearForm = useCallback(() => {
    const currentValues = watch()

    const clearedData = {
      ...defaultValues,
      name: currentValues.name,
      hsCodes: currentValues.hsCodes,
      importerCountryCode: currentValues.importerCountryCode,
      productName: currentValues.productName,
      currentCountryCode: currentValues.currentCountryCode
    }

    reset(clearedData)
  }, [reset, watch, defaultValues])

  const onSubmit = useCallback((val) => {
    const payload = {
      name: val.name,
      importerCountryCodes: [val.importerCountryCode.countryCode],
      currentCountryCode: val.currentCountryCode.countryCode,
      hsCodes: val.hsCodes.map((x) => x.value),
      productName: val.productName?.map(item => item.value || item) || [],
      b2bAccountId,
      firmSelectionAssist: true,
      emailContentAssist: true,
      ...(val.sender_company
        ? {
            sender_company: {
              ...val.sender_company,
              key_products: val.sender_company.key_products?.map(item => item.value || item) || [],
              facilities: val.sender_company.facilities?.map(item => item.value || item) || [],
              certifications: val.sender_company.certifications?.map(item => item.value || item) || [],
              market_experience: val.sender_company.market_experience?.map(item => item.value || item) || [],
              company_values: val.sender_company.company_values?.map(item => item.value || item) || []
            },
            email_purpose: {
              ...val.email_purpose,
              specific_products: val.email_purpose.specific_products?.map(item => item.value || item) || []
            },
            attachments: val.attachments || {}
          }
        : {})
    }

    setLoading(true)
    createCampaign(payload).then(() => {
      fetchCampaigns()
    }).catch((err) => {
      err && showError(t('mailTemplate.campaigns.modal.toastErr'))
    }).finally(() => {
      setLoading(false)
      handleClose()
      getUserDetailsAndProcess(true, userGSD, setUserData, true)
    })
  }, [b2bAccountId, fetchCampaigns, handleClose, setUserData, showError, t, userGSD])

  useEffect(() => {
    reset(defaultValues)
  }, [reset, defaultValues])

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 600, md: 700 },
          maxWidth: '90vw',
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16
        }
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{
          backgroundColor: 'white',
          p: 3,
          position: 'sticky',
          top: 0,
          zIndex: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <IconButton
            onClick={handleClose}
            sx={{
              mr: 2,
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <ArrowBackIcon/>
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
              {t('mailTemplate.campaigns.modal.title')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('mailTemplate.campaigns.modal.subTitle')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={t('mailTemplate.campaigns.modal.autofillTooltip')}>
              <Button
                variant="outlined"
                size="small"
                startIcon={autofillLoading ? <CircularProgress size={16}/> : <AutoFixHighIcon/>}
                onClick={handleAutofill}
                disabled={autofillLoading || loading}
                sx={{
                  minWidth: 'auto',
                  borderRadius: 2,
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    backgroundColor: 'primary.50'
                  }
                }}
              >
                {t('mailTemplate.campaigns.modal.autofill')}
              </Button>
            </Tooltip>
            <Tooltip title={t('mailTemplate.campaigns.modal.clearTooltip')}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ClearAllIcon/>}
                onClick={handleClearForm}
                disabled={loading}
                sx={{
                  minWidth: 'auto',
                  borderRadius: 2,
                  borderColor: 'text.secondary',
                  color: 'text.secondary',
                  '&:hover': {
                    borderColor: 'text.primary',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                {t('mailTemplate.campaigns.modal.clear')}
              </Button>
            </Tooltip>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <CloseIcon/>
          </IconButton>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off" noValidate style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1, overflow: 'auto', backgroundColor: '#f8fafc', pb: 2 }}>
            <Box sx={{ p: 3 }}>
              <CampaignInformation
                control={control}
                errors={errors}
                countries={countries}
              />

              <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mt: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                  {t('mailTemplate.wizard.ai.businessCompanyInfo')}
                </Typography>

                <BusinessCompanyInfoSection
                  control={control}
                  fieldPrefix="sender_company"
                  requiredFieldsOnly={false}
                />
              </Paper>

              <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mt: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                  {t('mailTemplate.wizard.ai.emailPurpose')}
                </Typography>

                <EmailPurposeTargetingSection
                  control={control}
                  fieldPrefix="email_purpose"
                  requiredFieldsOnly={false}
                />
              </Paper>
            </Box>
          </Box>

          <Paper sx={{
            p: 3,
            m: 0,
            borderRadius: 0,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end',
            backgroundColor: 'white',
            boxShadow: '0 -4px 12px rgba(0,0,0,0.05)',
            position: 'sticky',
            bottom: 0,
            zIndex: 1
          }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleClose}
              sx={{
                minWidth: 100,
                height: 44,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              {t('mailTemplate.campaigns.modal.cancel')}
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={loading || !isFormValid}
              startIcon={loading ? <CircularProgress sx={{ color: '#fff' }} size={20}/> : null}
              sx={{
                minWidth: 120,
                height: 44,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                background: 'linear-gradient(45deg, #0fc6b9 30%, #0ba89e 90%)',
                boxShadow: '0 3px 12px rgba(15, 198, 185, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #0eb3a6 30%, #0a998b 90%)',
                  boxShadow: '0 4px 16px rgba(15, 198, 185, 0.5)'
                },
                '&:disabled': {
                  background: 'rgba(15, 198, 185, 0.6)',
                  color: 'rgba(255, 255, 255, 0.8)'
                }
              }}
            >
              {t('mailTemplate.campaigns.modal.submit')}
            </Button>
          </Paper>
        </form>
      </Box>
    </Drawer>
  )
}

export default CreateCampaignDrawer
